// Owner 2026-10-29: lets a user's own bot (/api/paper-bot) drive the Auto command centre layout.
// Maps the command centre's Auto calls onto the user's bot endpoints. Everything stays on the user's own
// profile, buckets, bank and Betfair account; nothing reads or changes the owner's Auto.
import { API_BASE_URL } from '../config';
import { authHeader, readApiJson } from './auth';

async function raw(path: string, body?: any, signal?: AbortSignal) {
  const h: Record<string, string> = { ...authHeader(), ...(body ? { 'Content-Type': 'application/json' } : {}) };
  const r = await fetch(API_BASE_URL + path, { method: body ? 'POST' : 'GET', headers: h, cache: 'no-store', signal, body: body ? JSON.stringify(body) : undefined });
  return readApiJson(r, h.Authorization);
}
// One status read shared by the board and the balance tile (both poll).
let last: { at: number; p: Promise<any> } | null = null;
function bot(signal?: AbortSignal, fresh = false) {
  if (!fresh && last && Date.now() - last.at < 4000) return last.p;
  const p = raw('/api/paper-bot', undefined, signal);
  last = { at: Date.now(), p }; p.catch(() => { if (last?.p === p) last = null; });
  return p;
}
const editableAt = (start: string) => Date.parse(start) - Date.now() > 60000;

function board(d: any) {
  const p = d.profile, mode = p.mode === 'live' ? 'live' : 'paper';
  const scheduled = [
    ...(d.upcoming || []).map((u: any) => ({ queue_id: 'k:' + u.key, key: u.key, revision: 'u', queue_state: 'QUEUED', editable: !!u.key && editableAt(u.start_time),
      runner: u.runner, track: u.track, race: u.race, start_time: u.start_time, due_at: u.due_at, market_type: u.market_type,
      policy_id: u.policy_id, engine_label: u.label, stake: u.stake, manual_stake: u.manual_stake ?? null, staking: { method: 'bucket' },
      submission_hold: u.singles_off ? { reason: 'your Singles switch is OFF' } : null })),
    ...(d.manual_queue || []).map((m: any) => ({ queue_id: 'm:' + m._id, manual_id: m._id, revision: 'm', queue_state: 'QUEUED', editable: editableAt(m.bet.start_time),
      runner: m.bet.runner, track: m.bet.track, race: m.bet.race, start_time: m.bet.start_time, due_at: m.bet.start_time, market_type: m.bet.market_type,
      policy_id: m.bet.label ? 'OWNLEG|' : 'MANUAL|', engine_label: m.bet.label || 'Bet slip', stake: m.stake, manual_stake: m.stake, staking: { method: 'manual' } })),
  ].sort((a, b) => String(a.start_time).localeCompare(String(b.start_time)));
  const trades = (d.records || []).filter((r: any) => !['SKIPPED'].includes(r.state)).map((r: any) => ({ ...r, mode: r.mode === 'live' ? 'live' : 'paper', due_at: r.created_at,
    engine_label: r.policy_id, execution: { taken_price: r.bsp ?? null, bet_id: r.bet_id ?? null, price_basis: r.mode === 'live' ? 'Betfair SP' : 'Paper simulation' } }));
  return { user_bot: true, profile: p, summary: d.summary, betfair_connected: d.betfair_connected, live_funds: d.live_funds,
    control: { enabled: !!p.enabled, mode, armed: !!p.armed }, scheduled, held_submissions: [], trades,
    staking_buckets: [], staking_methods: {}, display_sections: { queue: { ready: true } }, system_culled: [],
    bot_health: { last_refresh_at: p.last_tick_at || null, last_error: p.last_error || null }, exchange_feedback: {} };
}

export async function userBotAutoRequest(path: string, body?: any, signal?: AbortSignal): Promise<any> {
  if (path === '/api/betfair/status') {
    const d = await bot(signal), live = d.profile.mode === 'live';
    const balance = live ? d.live_funds : d.summary?.available;
    return { configured: typeof balance === 'number', account: { balance } };
  }
  if (path.endsWith('/bet-override')) {
    const id = String(body.queue_id || ''), action = body.action === 'kill' ? 'skip' : 'stake';
    const target = id.startsWith('m:') ? { manual_id: id.slice(2) } : { key: id.slice(2) };
    const r = await raw('/api/paper-bot/bet-override', { ...target, action, ...(action === 'stake' ? { stake: body.stake } : {}) }, signal);
    last = null; return r;
  }
  if (path.endsWith('/bet-market')) {
    const id = String(body.queue_id || ''), target = id.startsWith('m:') ? { manual_id: id.slice(2) } : { key: id.slice(2) };
    const r = await raw('/api/paper-bot/bet-market', { ...target, action: body.action, stake: body.stake, label: body.label, runner: body.runner, track: body.track, race: body.race }, signal);
    last = null; return r;
  }
  if (path.startsWith('/api/rolling/simple-bsp') && !body) return board(await bot(signal, path.includes('refresh=1')));
  if (path === '/api/rolling/simple-bsp' && body) {
    // Start / stop / arm: save the full profile with only the requested switches changed.
    const p = (await bot(signal, true)).profile, next: any = { ...p };
    if (typeof body.enabled === 'boolean') next.enabled = body.enabled;
    if (body.mode === 'paper' || body.mode === 'live') { next.mode = body.mode; next.armed = false; }
    if (typeof body.armed === 'boolean') next.armed = body.armed && next.mode === 'live';
    const { revision, enabled, initial_bank, win_stake, place_stake, win_enabled, place_enabled, mode, armed } = next;
    const r = await raw('/api/paper-bot/settings', { revision, enabled, initial_bank, win_stake, place_stake, win_enabled, place_enabled, mode, armed }, signal);
    last = null; return r;
  }
  return raw(path, body, signal);
}
